/**
 * Per-source volume, mute and solo into a master bus (design §7.4).
 *
 * @module
 */

/** Gain changes ramp over about this long, so they don't click. */
const RAMP_S = 0.01;

export interface SourceMix {
  volume: number;
  muted: boolean;
  solo: boolean;
}

export const DEFAULT_MIX: SourceMix = { volume: 1, muted: false, solo: false };

interface Strip {
  /** Where the source's audio goes in; insert per-source processing between it and `gain`. */
  input: GainNode;
  gain: GainNode;
  meter: AnalyserNode;
  mix: SourceMix;
}

function analyser(ctx: BaseAudioContext, fftSize: number): AnalyserNode {
  const a = ctx.createAnalyser();
  a.fftSize = fftSize;
  a.smoothingTimeConstant = 0;
  return a;
}

export class Mixer {
  readonly master: GainNode;
  /** The mixed output, for the spectrum. */
  readonly masterMeter: AnalyserNode;
  private readonly strips = new Map<string, Strip>();
  private processor: AudioNode | null = null;

  constructor(private readonly ctx: BaseAudioContext) {
    this.master = ctx.createGain();
    this.masterMeter = analyser(ctx, 2048);
    this.master.connect(this.masterMeter);
    this.masterMeter.connect(ctx.destination);
  }

  /** Puts a processor (the AGC) between the mix and its meter and output. */
  setProcessor(node: AudioNode | null): void {
    this.master.disconnect();
    this.processor?.disconnect();
    this.processor = node;
    if (node) {
      this.master.connect(node);
      node.connect(this.masterMeter);
    } else {
      this.master.connect(this.masterMeter);
    }
  }

  /** The node a source's audio connects to. */
  input(key: string): AudioNode {
    let s = this.strips.get(key);
    if (!s) {
      const input = this.ctx.createGain();
      const gain = this.ctx.createGain();
      const meter = analyser(this.ctx, 2048);
      input.connect(gain);
      gain.connect(this.master);
      gain.connect(meter);
      s = { input, gain, meter, mix: { ...DEFAULT_MIX } };
      this.strips.set(key, s);
      this.apply();
    }
    return s.input;
  }

  /** What a source contributes to the mix, after its volume, mute and solo. */
  meter(key: string): AnalyserNode | null {
    return this.strips.get(key)?.meter ?? null;
  }

  get(key: string): SourceMix {
    return { ...(this.strips.get(key)?.mix ?? DEFAULT_MIX) };
  }

  set(key: string, mix: Partial<SourceMix>): void {
    this.input(key);
    const s = this.strips.get(key);
    if (!s) return;
    s.mix = { ...s.mix, ...mix, volume: Math.max(0, mix.volume ?? s.mix.volume) };
    this.apply();
  }

  /** While any source is soloed only soloed sources are heard; mute always silences. */
  audible(key: string): boolean {
    const mix = this.get(key);
    const soloing = [...this.strips.values()].some((s) => s.mix.solo);
    return !mix.muted && (!soloing || mix.solo);
  }

  private apply(): void {
    for (const [key, s] of this.strips) {
      const target = this.audible(key) ? s.mix.volume : 0;
      s.gain.gain.setTargetAtTime(target, this.ctx.currentTime, RAMP_S / 3);
    }
  }
}

type SinkContext = AudioContext & { setSinkId?: (id: string) => Promise<void> };

/** Whether audio can be sent to a chosen output device (Chromium; not WebKit). */
export function canChooseOutput(ctx: AudioContext): boolean {
  return typeof (ctx as SinkContext).setSinkId === "function";
}

/** Sends the context's output to a device; "" is the default output. */
export async function chooseOutput(ctx: AudioContext, deviceId: string): Promise<void> {
  const sink = (ctx as SinkContext).setSinkId;
  if (sink) await sink.call(ctx, deviceId);
}

export async function outputDevices(): Promise<{ id: string; label: string }[]> {
  const all = (await navigator.mediaDevices?.enumerateDevices()) ?? [];
  return all
    .filter((d) => d.kind === "audiooutput" && d.deviceId !== "default" && d.deviceId !== "")
    .map((d, i) => ({ id: d.deviceId, label: d.label || `Output ${i + 1}` }));
}
